import { useState, useMemo } from 'react';
import { Copy, Download, RefreshCw } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { copyToClipboard, download, uuid } from '@/lib/utils';
import { toast } from 'sonner';

const FIRST = ['Aarav','Priya','Rohan','Anaya','Vivaan','Ishaan','Diya','Kabir','Neha','Arjun','Maya','Aditya','Sara','Kunal','Tara','Ravi','Meera','Dev','Riya','Vikram'];
const LAST = ['Sharma','Mehta','Iyer','Patel','Khanna','Rao','Reddy','Nair','Singh','Kumar','Gupta','Banerjee','Bose','Joshi','Verma','Pillai','Malhotra'];
const COMPANIES = ['Acme','Globex','Initech','Massive Dynamic','Hooli','Pied Piper','Stark Industries','Wayne Enterprises','Umbrella Corp','Cyberdyne'];
const DOMAINS = ['utility.os','example.com','mail.dev','test.io','example.org'];
const ROLES = ['Engineer','Designer','PM','Analyst','Marketer','Operator','Founder','Researcher','Consultant'];
const CITIES = ['Mumbai','Bengaluru','Delhi','Pune','Hyderabad','Chennai','Kolkata','Ahmedabad','Jaipur','Goa'];

function rand<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

interface Person {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  role: string;
  city: string;
  age: number;
  joined: string;
  active: boolean;
}

function genPerson(): Person {
  const first = rand(FIRST);
  const last = rand(LAST);
  return {
    id: uuid(),
    firstName: first,
    lastName: last,
    email: `${first.toLowerCase()}.${last.toLowerCase()}@${rand(DOMAINS)}`,
    phone: `+91 9${Math.floor(100000000 + Math.random() * 899999999)}`,
    company: rand(COMPANIES),
    role: rand(ROLES),
    city: rand(CITIES),
    age: 21 + Math.floor(Math.random() * 35),
    joined: new Date(Date.now() - Math.floor(Math.random() * 1000) * 86400_000)
      .toISOString()
      .slice(0, 10),
    active: Math.random() > 0.25,
  };
}

function toCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const esc = (v: unknown) => {
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  return [headers.join(','), ...rows.map((r) => headers.map((h) => esc(r[h])).join(','))].join('\n');
}

function toSql(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const escVal = (v: unknown) =>
    typeof v === 'number' || typeof v === 'boolean'
      ? String(v)
      : `'${String(v).replace(/'/g, "''")}'`;
  return rows
    .map(
      (r) =>
        `INSERT INTO users (${headers.join(', ')}) VALUES (${headers.map((h) => escVal(r[h])).join(', ')});`,
    )
    .join('\n');
}

type Format = 'json' | 'csv' | 'sql';

export function MockData() {
  const [count, setCount] = useState(20);
  const [seed, setSeed] = useState(0);
  const [format, setFormat] = useState<Format>('json');

  const data = useMemo(() => {
    return Array.from({ length: count }, () => genPerson());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, seed]);

  const output = useMemo(() => {
    if (format === 'json') return JSON.stringify(data, null, 2);
    if (format === 'csv') return toCsv(data as unknown as Record<string, unknown>[]);
    return toSql(data as unknown as Record<string, unknown>[]);
  }, [data, format]);

  return (
    <div>
      <ToolHeader
        toolId="mock-data"
        title="Mock data generator"
        description="Generate realistic-looking user records as JSON, CSV, or SQL inserts."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-4">
          <Field label="Records">
            <Input
              type="number"
              value={count}
              onChange={(e) =>
                setCount(Math.max(1, Math.min(500, Number(e.target.value) || 1)))
              }
              min={1}
              max={500}
            />
          </Field>
          <Tabs value={format} onValueChange={(v) => setFormat(v as Format)}>
            <TabsList className="w-full">
              <TabsTrigger value="json" className="flex-1">JSON</TabsTrigger>
              <TabsTrigger value="csv" className="flex-1">CSV</TabsTrigger>
              <TabsTrigger value="sql" className="flex-1">SQL</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="secondary" size="sm" onClick={() => setSeed((s) => s + 1)}>
              <RefreshCw size={11} />
              Reroll
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={async () => {
                const ok = await copyToClipboard(output);
                if (ok) toast.success('Copied');
              }}
            >
              <Copy size={11} />
              Copy
            </Button>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            onClick={() => {
              const ext = format === 'json' ? 'json' : format === 'csv' ? 'csv' : 'sql';
              const mime =
                format === 'json' ? 'application/json' : 'text/plain';
              download(output, `mock.${ext}`, mime);
            }}
          >
            <Download size={11} />
            Download
          </Button>
        </Card>

        <Card className="lg:col-span-2 lg:order-1 !p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <span className="text-xs font-medium text-fg-muted">Preview</span>
            <span className="text-2xs text-fg-subtle font-mono">
              {data.length} records · {format.toUpperCase()}
            </span>
          </div>
          <pre className="font-mono text-[12px] leading-relaxed p-4 overflow-auto max-h-[520px]">
            <code>{output}</code>
          </pre>
        </Card>
      </div>
    </div>
  );
}

export default MockData;
